import {
  ExternalCalendarEventPreview,
  ExternalEventResponseState,
  ID,
} from '@griply/types';
import React, { useRef } from 'react';
import {
  useDuplicateEvent,
  useExternalEventMenu,
} from 'shared/contexts/event-form';
import { useContextMenuState } from 'shared/hooks/use-context-menu-state';
import { useOpenExternalEventPopup } from 'shared/hooks/use-open-external-event-popup';
import { toEventPopupBoundary } from 'shared/lib/full-calendar';

import * as Styled from './event.style';
import { EventCard } from './event-card';
import { useTimeBasedStyle } from './use-time-based-style';

export type ExternalEventProps = {
  externalId: ID;
  name: string;
  url?: string;
  color?: string;
  // absent on responses from an older backend — falls back to opening the url
  connectionId?: ID;
  calendarId?: ID;
  isRecurring?: boolean;
  // absent on an older backend; only an explicit `false` marks you as an invitee
  isOrganizer?: boolean;
  responseState?: ExternalEventResponseState;
  preview?: ExternalCalendarEventPreview;
  allDay?: boolean;
  startDate?: string;
  endDate?: string;
  timeText?: string;
  isPast?: boolean;
};

export const ExternalEvent: React.FC<ExternalEventProps> = ({
  externalId,
  name,
  url,
  color,
  connectionId,
  calendarId,
  isRecurring,
  isOrganizer,
  responseState,
  preview,
  allDay,
  startDate,
  endDate,
  timeText,
  isPast,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { numberOfLines } = useTimeBasedStyle(startDate, endDate);
  const { eventTarget, onOpen: onEdit } = useOpenExternalEventPopup(
    containerRef,
    {
      externalId,
      title: name,
      allDay: !!allDay,
      start: toEventPopupBoundary(startDate, !!allDay),
      end: toEventPopupBoundary(endDate, !!allDay),
      connectionId,
      calendarId,
      url,
      preview,
    },
  );

  const {
    open: openMenu,
    close: closeMenu,
    position: menuPosition,
  } = useContextMenuState({ disabled: !eventTarget });

  const duplicateEvent = useDuplicateEvent();
  const ExternalEventMenu = useExternalEventMenu();
  const onDuplicate = eventTarget
    ? () => duplicateEvent(eventTarget)
    : undefined;

  const onOpenUrl = () => window.open(url, '_blank', 'noreferrer');

  const _onClick = eventTarget ? onEdit : url ? onOpenUrl : undefined;

  return (
    <>
      <EventCard
        ref={containerRef}
        title={name}
        color={color}
        timeText={timeText}
        isPast={isPast}
        numberOfLines={numberOfLines}
        outlined={responseState === ExternalEventResponseState.AwaitingResponse}
        onClick={_onClick}
        onContextMenu={openMenu}
        leading={
          <Styled.ColorBarSlot
            $color={color}
            $past={isPast}
            $responseState={responseState}
          />
        }
      />
      {!!menuPosition && !!eventTarget && (
        <ExternalEventMenu
          isOrganizer={isOrganizer}
          eventTarget={eventTarget}
          isRecurring={!!isRecurring}
          location={menuPosition}
          onEdit={onEdit}
          onDuplicate={onDuplicate}
          onClose={closeMenu}
        />
      )}
    </>
  );
};
